import { Injectable } from "@angular/core";
import { ApicallService } from "@core-services/apicall.service";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { PaginationService } from "../../shared/services/pagination.service";
import { AssetScroll } from "@models/asset_scroll";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { CoreStoreFacade } from "@modules/+new/core/core-store/domain/+state/core-store.facade";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";

@Injectable()
export class AssetsDropdownScrollService extends PaginationService<BpSelectOption> {
	private currentSelectedTradable: Tradable = null;

	public constructor(
		public apiCallService: ApicallService,
		private readonly walletService: TradableWalletListService,
		private tradableListService: TradableListService,
		protected readonly coreStoreFacade: CoreStoreFacade
	) {
		super(coreStoreFacade, apiCallService, "assets", "v3", Tradable);
	}

	public convertPaginationList(list: AssetScroll[]): BpSelectOption[] {
		if (!list) {
			return [];
		}

		const tradableList: Tradable[] = list
			.map((a: AssetScroll) => this.tradableListService.list.find((t) => t.getId() === a.getId()))
			.filter(
				(i) =>
					!!i &&
					!i.isIndexOnly() &&
					i.isBuyActive() &&
					!(i instanceof LeveragedToken || i instanceof SecurityToken)
			);
		return this.placeCurrentAsset(tradableList).map(
			(t: Tradable) =>
				({
					name: t.getName(),
					iconUrl: t.getLogo(),
					value: this.getWalletForTradable(t)
				}) as BpSelectOption
		);
	}

	// ---------------------- BELOW: necessary for tracking the current dropdown selection

	public setCurrentTradable(tradable: Tradable): void {
		this.currentSelectedTradable = tradable;
	}

	private placeCurrentAsset(tradableList: Tradable[]): Tradable[] {
		const needsNextPage: boolean = this.getLastEvent().getNextPageRequired();
		if (!this.currentSelectedTradable) {
			return tradableList;
		}
		const selectionAlreadyInPagination: boolean = this.getListValue().some((item) =>
			this.selectionIsEqual(item.value.getCurrencyId())
		);
		// If the next page is fetched and the current selection is already part of the list then it should NOT be included twice
		if (needsNextPage && selectionAlreadyInPagination) {
			if (this.selectionIsIncluded(tradableList)) {
				return tradableList.filter((t) => t.getId() !== this.currentSelectedTradable.getId());
			}
		} else {
			// Unshift to place selected item on top if not part of the currently fetched pages AND is not part of the current selection
			if (!this.selectionIsIncluded(tradableList)) {
				tradableList.unshift(this.currentSelectedTradable);
			}
		}
		return tradableList;
	}

	private selectionIsEqual(id: string): boolean {
		return id === this.currentSelectedTradable.getId();
	}

	private selectionIsIncluded(tradableList: Tradable[]): boolean {
		return tradableList.some((t) => this.selectionIsEqual(t.getId()));
	}

	private getWalletForTradable(t: Tradable): TradableWallet {
		const wallets = this.walletService.getWalletsByTradableId(t.getId());

		// If we have more than 1 wallet we need to make sure that dummy wallet isn't one of them
		if (wallets.length > 1) {
			return wallets.find((w) => w.isDefault() && !w.isNew());
		}

		return wallets.find((w) => w.isDefault());
	}
}
